import { useCallback, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "@/utils/apiConfig";

/**
 * Kaiso Prime state for the customer portal.
 *
 * Membership is never inferred from a click: every value here comes from the
 * backend, which only reports `is_prime` after a gateway payment is confirmed.
 */
export function usePortalPrime(restaurantId) {
  const [primeStatus, setPrimeStatus] = useState(null);
  const [primeOffers, setPrimeOffers] = useState([]);

  const storedPhone = useCallback(
    () => localStorage.getItem(`customer_phone_${restaurantId}`),
    [restaurantId],
  );

  const refreshPrime = useCallback(async (rawPhone) => {
    const phone = rawPhone || storedPhone();
    if (!phone) return;
    const query = `restaurant_id=${encodeURIComponent(restaurantId)}&phone=${encodeURIComponent(phone)}`;
    try {
      const res = await axios.get(`${API_URL}/api/prime/status?${query}`);
      setPrimeStatus({ ...res.data, phone });
      if (!res.data?.is_prime) {
        setPrimeOffers([]);
        return;
      }
      const offers = await axios.get(`${API_URL}/api/prime/offers?${query}`);
      setPrimeOffers(offers.data?.offers || []);
    } catch (e) {
      console.error("Error fetching Prime status:", e);
    }
  }, [restaurantId, storedPhone]);

  /**
   * Called when the customer comes back from the gateway. It asks the backend
   * to settle the intent; the backend still requires gateway proof, so this
   * cannot activate Prime on its own.
   */
  const settlePendingPrimePayment = useCallback(async () => {
    let intentId = null;
    try { intentId = localStorage.getItem(`prime_intent_${restaurantId}`); } catch { intentId = null; }
    if (!intentId) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/prime/payment-status?restaurant_id=${encodeURIComponent(restaurantId)}`
        + `&intent_id=${encodeURIComponent(intentId)}`,
      );
      if (res.data?.status !== "paid") return;
      try { localStorage.removeItem(`prime_intent_${restaurantId}`); } catch { /* private mode */ }
      toast.success("¡Pago confirmado! Ya eres miembro PRIME 🌟");
      await refreshPrime();
    } catch (e) {
      console.error("Error settling Prime payment:", e);
    }
  }, [restaurantId, refreshPrime]);

  const cancelPrime = useCallback(async () => {
    const phone = storedPhone();
    if (!phone) return;
    try {
      await axios.post(`${API_URL}/api/prime/cancel`, { restaurant_id: restaurantId, phone });
      toast.success("Suscripción PRIME cancelada.");
      await refreshPrime(phone);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cancelar PRIME");
    }
  }, [restaurantId, refreshPrime, storedPhone]);

  return { primeStatus, primeOffers, refreshPrime, settlePendingPrimePayment, cancelPrime };
}
